import { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import "./FastBoundSyncDashboard.scss";
import OwButton from "../../../../shared/OwButton/OwButton";
import OwLabel from "../../../../shared/OwLabel/OwLabel";
import OwSelectOption from "../../../../shared/OwSelectOption/OwSelectOption";
import IFastbound_Account from "../../../../../models/IFastbound_Account";
import IErrorDTO from "../../../../../models/DTO/IErrorDTO";
import INotification from "../../../../../models/INotification";
import NotificationLevelEnum from "../../../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../../../models/NotificationPositionEnum";
import UserContext from "../../../../../contexts/UserContext";
import SupplierContext from "../../../../../contexts/SupplierContext";
import FastBoundService, { FastBoundDashboardResponse, FastBoundQueueStatusResponse } from "../../../../../services/FastBoundService";
import UserService from "../../../../../services/UserService";
import IFastbound_Queue from "../../../../../models/IFastbound_Queue";
import { useIntl } from "react-intl";

type Props = {
    accounts: IFastbound_Account[];
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
    refreshKey?: number;
};

const queueStatusBadge: Record<number, string> = {
    1: "secondary", // NOT_STARTED
    2: "info", // PROCESSING
    3: "success", // COMPLETED
    4: "danger", // ERRORED
    6: "warning", // CANCELLED
};

const queueStatusLabel: Record<number, string> = {
    1: "Pending",
    2: "Processing",
    3: "Completed",
    4: "Errored",
    6: "Cancelled",
};

export const FastBoundSyncDashboard = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const intl = useIntl();

    const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
    const [dashboardData, setDashboardData] = useState<FastBoundDashboardResponse | null>(null);
    const [queueStatus, setQueueStatus] = useState<FastBoundQueueStatusResponse | null>(null);
    const [isSyncingAcquisitions, setIsSyncingAcquisitions] = useState(false);
    const [isSyncingContacts, setIsSyncingContacts] = useState(false);
    const [isSyncingItems, setIsSyncingItems] = useState(false);
    const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        if (props.accounts.length > 0 && !selectedAccountId) {
            setSelectedAccountId(props.accounts[0].id);
        }
    }, [props.accounts]);

    useEffect(() => {
        if (selectedAccountId) {
            loadDashboard();
        }
        return () => {
            stopPolling();
        };
    }, [selectedAccountId]);

    useEffect(() => {
        if (selectedAccountId && props.refreshKey) {
            loadDashboard();
        }
    }, [props.refreshKey]);

    const loadDashboard = async () => {
        if (!selectedAccountId) return;

        try {
            const data = await FastBoundService.getDashboard(selectedAccountId, userModel, supplierModel);
            setDashboardData(data);

            // Check if there are active queue items and start polling
            if (data.activeQueueItems && data.activeQueueItems.length > 0) {
                startPolling();
            } else {
                stopPolling();
            }
        } catch (err: any) {
            console.error("Failed to load dashboard:", err);
        }
    };

    const loadQueueStatus = async () => {
        if (!selectedAccountId) return;

        try {
            const data = await FastBoundService.getQueueStatus(selectedAccountId, userModel, supplierModel);
            setQueueStatus(data);

            if (data.activeCount === 0) {
                stopPolling();
                // Reload full dashboard to get updated stats
                loadDashboard();
            }
        } catch (err: any) {
            console.error("Failed to load queue status:", err);
        }
    };

    const startPolling = () => {
        if (pollIntervalRef.current) return;
        pollIntervalRef.current = setInterval(loadQueueStatus, 8000);
    };

    const stopPolling = () => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    };

    const handleAccountChange = (e: ChangeEvent<any>) => {
        const value = e.target.value ? Number(e.target.value) : null;
        setSelectedAccountId(value);
    };

    const handleSyncAcquisitions = async () => {
        if (!selectedAccountId) return;
        setIsSyncingAcquisitions(true);

        try {
            await FastBoundService.syncAcquisitions(selectedAccountId, userModel, supplierModel);
            props.notifyFn(null, {
                message: "acquisition_sync_queued",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            });
            startPolling();
            loadDashboard();
        } catch (err: any) {
            props.notifyFn(null, {
                title: err.message,
                message: err.description || err.message || "failed_to_sync_acquisitions",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
                skipMessageTranslate: true,
            });
        } finally {
            setIsSyncingAcquisitions(false);
        }
    };

    const handleSyncContacts = async () => {
        if (!selectedAccountId) return;
        setIsSyncingContacts(true);

        try {
            await FastBoundService.syncContacts(selectedAccountId, userModel, supplierModel);
            props.notifyFn(null, {
                message: "contact_sync_queued",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            });
            startPolling();
            loadDashboard();
        } catch (err: any) {
            props.notifyFn(null, {
                title: err.message,
                message: err.description || err.message || "failed_to_sync_contacts",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
                skipMessageTranslate: true,
            });
        } finally {
            setIsSyncingContacts(false);
        }
    };

    const handleSyncItems = async () => {
        if (!selectedAccountId) return;
        setIsSyncingItems(true);

        try {
            await FastBoundService.syncItems(selectedAccountId, userModel, supplierModel);
            props.notifyFn(null, {
                message: "item_sync_queued",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            });
            startPolling();
            loadDashboard();
        } catch (err: any) {
            props.notifyFn(null, {
                title: err.message,
                message: err.description || err.message || "failed_to_sync_items",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
                skipMessageTranslate: true,
            });
        } finally {
            setIsSyncingItems(false);
        }
    };

    const getSelectedAccount = (): IFastbound_Account | undefined => {
        if (!dashboardData) return undefined;
        return dashboardData.accounts?.find((a) => a.id === selectedAccountId);
    };

    const getActiveQueueItems = (): IFastbound_Queue[] => {
        // Prefer live polling data, fall back to dashboard data
        if (queueStatus) {
            return [...(queueStatus.processingItems || []), ...(queueStatus.pendingItems || [])];
        }
        return dashboardData?.activeQueueItems || [];
    };

    const selectedAccount = getSelectedAccount();
    const activeQueueItems = getActiveQueueItems();

    if (props.accounts.length === 0) return null;

    return (
        <div className={"FastBoundSyncDashboard mt-3"}>
            <div className={"d-flex align-items-center mb-3"}>
                <OwLabel title={"sync_dashboard"} isBold={true} className={"me-3"} />
                <div className={"w-15r"}>
                    <OwSelectOption
                        options={props.accounts}
                        valueProp={"id"}
                        titleProp={"name"}
                        name={"selectedAccountId"}
                        selectedOption={{ selectedAccountId }}
                        selectedOptionProp={"selectedAccountId"}
                        fn={handleAccountChange}
                    />
                </div>
            </div>

            {selectedAccount && (
                <>
                    <div className={"stat-cards-row"}>
                        <div className={"stat-card"}>
                            <div className={"stat-label"}>
                                <OwLabel title={"serials_imported"} />
                            </div>
                            <div className={"stat-value"}>{selectedAccount.total_serials_imported || 0}</div>
                            <div className={"stat-sub"}>
                                {selectedAccount.last_acquisition_sync_at
                                    ? UserService.parseLastUpdated(selectedAccount.last_acquisition_sync_at, true, true, true)
                                    : "N/A"}
                            </div>
                        </div>
                        <div className={"stat-card"}>
                            <div className={"stat-label"}>
                                <OwLabel title={"contacts_synced"} />
                            </div>
                            <div className={"stat-value"}>{selectedAccount.total_contacts_synced || 0}</div>
                            <div className={"stat-sub"}>
                                {selectedAccount.last_contact_sync_at
                                    ? UserService.parseLastUpdated(selectedAccount.last_contact_sync_at, true, true, true)
                                    : "N/A"}
                            </div>
                        </div>
                        <div className={"stat-card"}>
                            <div className={"stat-label"}>
                                <OwLabel title={"last_item_sync"} />
                            </div>
                            <div className={"stat-value"}>
                                {selectedAccount.last_item_sync_at
                                    ? UserService.parseLastUpdated(selectedAccount.last_item_sync_at, true, true, true)
                                    : "N/A"}
                            </div>
                        </div>
                        {selectedAccount.disposition_type && (
                            <div className={"stat-card"}>
                                <div className={"stat-label"}>
                                    <OwLabel title={"disposition_type"} />
                                </div>
                                <div className={"stat-value"}>{selectedAccount.disposition_type}</div>
                            </div>
                        )}
                        {selectedAccount.disposition_category && (
                            <div className={"stat-card"}>
                                <div className={"stat-label"}>
                                    <OwLabel title={"disposition_category"} />
                                </div>
                                <div className={"stat-value"}>{selectedAccount.disposition_category}</div>
                            </div>
                        )}
                        {selectedAccount.nfa_disposition_type && (
                            <div className={"stat-card"}>
                                <div className={"stat-label"}>
                                    <OwLabel title={"nfa_disposition_type"} />
                                </div>
                                <div className={"stat-value"}>{selectedAccount.nfa_disposition_type}</div>
                            </div>
                        )}
                        {selectedAccount.acquisition_type_filter && (
                            <div className={"stat-card"}>
                                <div className={"stat-label"}>
                                    <OwLabel title={"acquisition_type_filter"} />
                                </div>
                                <div className={"stat-value"}>
                                    {selectedAccount.acquisition_type_filter.charAt(0).toUpperCase() +
                                        selectedAccount.acquisition_type_filter.slice(1)}
                                </div>
                            </div>
                        )}
                        <div className={"stat-card"}>
                            <div className={"stat-label"}>
                                <OwLabel title={"serial_validation"} />
                            </div>
                            <div className={"stat-value"}>{selectedAccount.serial_format_pattern ? "Active" : "None"}</div>
                        </div>
                        {dashboardData && dashboardData.errorCount > 0 && (
                            <div className={"stat-card"}>
                                <div className={"stat-label"}>
                                    <OwLabel title={"errors"} />
                                </div>
                                <div className={"stat-value"}>
                                    <span className={"badge bg-danger"}>{dashboardData.errorCount}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={"d-flex mb-3"}>
                        <OwButton
                            title={"sync_acquisitions"}
                            fn={handleSyncAcquisitions}
                            active={isSyncingAcquisitions}
                            disableOnActive={true}
                            leftIconClass={"fad fa-sync"}
                            className={"btn-outline-primary me-2"}
                        />
                        <OwButton
                            title={"sync_contacts"}
                            fn={handleSyncContacts}
                            active={isSyncingContacts}
                            disableOnActive={true}
                            leftIconClass={"fad fa-sync"}
                            className={"btn-outline-primary me-2"}
                        />
                        <OwButton
                            title={"sync_items"}
                            fn={handleSyncItems}
                            active={isSyncingItems}
                            disableOnActive={true}
                            leftIconClass={"fad fa-sync"}
                            className={"btn-outline-primary"}
                        />
                    </div>

                    {activeQueueItems.length > 0 && (
                        <div className={"mb-3"}>
                            <OwLabel title={"active_queue"} isBold={true} />
                            {activeQueueItems.map((item) => {
                                const progress =
                                    item.total_items && item.total_items > 0
                                        ? Math.round(((item.processed_items || 0) / item.total_items) * 100)
                                        : 0;
                                const statusId = item.queue_status_id || 1;

                                const typeName = intl.formatMessage({ id: item.type });

                                return (
                                    <div key={item.id} className={"d-flex align-items-center mb-1"}>
                                        <span className={`badge bg-${queueStatusBadge[statusId] || "secondary"} me-2`}>
                                            {queueStatusLabel[statusId] || "Unknown"}
                                        </span>
                                        <span className={"me-2 small"}>{typeName}</span>
                                        <div
                                            className={"flex-grow-1 me-2"}
                                            style={{ height: "6px", background: "var(--minor-bg-color)", borderRadius: "3px" }}>
                                            <div
                                                style={{
                                                    width: `${progress}%`,
                                                    height: "100%",
                                                    borderRadius: "3px",
                                                    transition: "width 0.3s ease",
                                                }}
                                                className={"bg-primary"}
                                            />
                                        </div>
                                        <span className={"small"}>
                                            {item.processed_items || 0} / {item.total_items || 0}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
